import React from 'react';
import { ProcessedImage, ImageFormat, ImageSize } from '../services/imageProcessingService';

interface PictureProps {
  images: ProcessedImage[];
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  sizes?: string; // CSS sizes attribute for responsive images
}

/**
 * Picture Component
 * Implements PRD requirement: Images must be served using <picture> with ordered fallbacks
 * Supports AVIF -> WebP -> JPEG fallback chain
 */
export const Picture: React.FC<PictureProps> = ({
  images,
  alt,
  className = '',
  loading = 'lazy',
  onLoad,
  onError,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
}) => {
  // Group images by format
  const imagesByFormat = images.reduce((acc, img) => {
    if (!acc[img.format]) {
      acc[img.format] = [];
    }
    acc[img.format].push(img);
    return acc;
  }, {} as Record<ImageFormat, ProcessedImage[]>);

  // Sort formats by priority: avif > webp > jpeg
  const formatPriority: ImageFormat[] = ['avif', 'webp', 'jpeg'];
  const availableFormats = formatPriority.filter(f => imagesByFormat[f]?.length > 0);

  // Generate srcset for a format
  const generateSrcSet = (formatImages: ProcessedImage[]): string => {
    return formatImages
      .sort((a, b) => a.size - b.size)
      .map(img => `${img.url} ${img.size}w`)
      .join(', ');
  };

  // Get largest JPEG as fallback
  const fallbackImage = images
    .filter(img => img.format === 'jpeg')
    .sort((a, b) => b.size - a.size)[0];

  if (!fallbackImage) {
    console.warn('No JPEG fallback image available');
    return null;
  }

  return (
    <picture>
      {/* AVIF sources (if available) */}
      {imagesByFormat.avif && imagesByFormat.avif.length > 0 && (
        <source
          type="image/avif"
          srcSet={generateSrcSet(imagesByFormat.avif)}
          sizes={sizes}
        />
      )}
      
      {/* WebP sources (if available) */}
      {imagesByFormat.webp && imagesByFormat.webp.length > 0 && (
        <source
          type="image/webp"
          srcSet={generateSrcSet(imagesByFormat.webp)}
          sizes={sizes}
        />
      )}
      
      {/* JPEG fallback */}
      <img
        src={fallbackImage.url}
        srcSet={generateSrcSet(images.filter(img => img.format === 'jpeg'))}
        sizes={sizes}
        alt={alt}
        className={className}
        loading={loading}
        onLoad={onLoad}
        onError={onError}
        draggable={false}
      />
    </picture>
  );
};
